import { useEffect, useState } from "react";
import { Polyline, Polygon, CircleMarker, Tooltip } from "react-leaflet";
import LeafletMap, { FitBounds } from "@/components/map/LeafletMap";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Search, Route, Save, Trash2, MapPin, FlaskConical } from "lucide-react";
import { toast } from "sonner";

const WIDTHS = [0.5, 1, 2, 5];
const CAT_COLORS = { bicycle: "#22c55e", cafe: "#f59e0b", restaurant: "#ef4444", hotel: "#8b5cf6" };

export default function TrailLabPanel() {
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [trail, setTrail] = useState(null);
  const [width, setWidth] = useState(1);
  const [corridor, setCorridor] = useState(null);
  const [territoryId, setTerritoryId] = useState(null);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [summary, setSummary] = useState({});
  const [busy, setBusy] = useState("");
  const [drafts, setDrafts] = useState([]);

  const loadDrafts = async () => { try { const { data } = await api.get("/admin/gis/territories"); setDrafts(data); } catch { /* noop */ } };
  useEffect(() => { loadDrafts(); }, []);

  const reset = () => { setTrail(null); setCorridor(null); setTerritoryId(null); setBusinesses([]); setSummary({}); setName(""); setNotes(""); };

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true); setCandidates([]);
    try {
      const { data } = await api.get("/admin/gis/trail-search", { params: { name: q.trim(), state: state.trim() || undefined } });
      setCandidates(data.candidates);
      if (!data.candidates.length) toast.info("No trails matched. Try a different name or add a state.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Overpass search failed"); }
    finally { setSearching(false); }
  };

  const importTrail = async (c) => {
    reset(); setBusy("import");
    try {
      const { data } = await api.post("/admin/gis/trail-import", { name: c.name, region_state: c.region_state, coordinates: c.coordinates, osm_id: c.osm_id });
      setTrail(data); setName(`${data.name} — ${width}mi`);
      toast.success(`Imported ${data.name} (${data.length_miles} mi)`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const genCorridor = async (w) => {
    setWidth(w); setBusy("corridor");
    try {
      const { data } = await api.post("/admin/gis/territory/preview", { trail_id: trail.id, buffer_miles: w });
      setCorridor(data.polygon); setTerritoryId(null); setBusinesses([]); setSummary({}); setName(`${trail.name} — ${w}mi`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const saveDraft = async () => {
    setBusy("save");
    try {
      const { data } = await api.post("/admin/gis/territory", { trail_id: trail.id, buffer_miles: width, name, notes });
      setTerritoryId(data.id); toast.success("Draft territory saved"); loadDrafts();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const findBusinesses = async () => {
    if (!territoryId) { toast.info("Save the draft first, then find businesses."); return; }
    setBusy("biz");
    try {
      const { data } = await api.post(`/admin/gis/territory/${territoryId}/businesses`);
      setBusinesses(data.businesses); setSummary(data.summary);
      toast.success(`Found ${data.count} businesses in this territory`);
      loadDrafts();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const openDraft = async (id) => {
    setBusy("open");
    try {
      const { data } = await api.get(`/admin/gis/territory/${id}`);
      setTrail(data.trail); setCorridor(data.polygon); setTerritoryId(data.id);
      setWidth(data.buffer_miles); setName(data.name); setNotes(data.notes || "");
      setBusinesses(data.businesses || []); setSummary(data.business_summary || {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const deleteDraft = async (id) => {
    try { await api.delete(`/admin/gis/territory/${id}`); toast.success("Draft deleted"); if (id === territoryId) reset(); loadDrafts(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const bounds = corridor || (trail && trail.coordinates) || null;

  return (
    <div className="space-y-4" data-testid="trail-lab-panel">
      <div className="flex items-center gap-2">
        <FlaskConical className="w-5 h-5 text-primary" />
        <div>
          <h2 className="font-heading text-lg font-bold">Trail Lab</h2>
          <p className="text-sm text-muted-foreground">Phase 0 — search a trail, build a territory corridor, and preview nearby businesses. Drafts only; nothing is published.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-4">
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" placeholder="Trail name (e.g. Cape Cod Rail Trail)" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="traillab-search-input" /></div>
              <Input className="w-24" placeholder="State" value={state} onChange={(e) => setState(e.target.value)} data-testid="traillab-state-input" />
            </div>
            <Button className="w-full" onClick={search} disabled={searching} data-testid="traillab-search-btn">{searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}Search trails</Button>
            {candidates.map((c) => (
              <button key={c.osm_id} onClick={() => importTrail(c)} disabled={busy === "import"} className="w-full text-left rounded-lg border border-border hover:border-primary/50 bg-background/50 p-3" data-testid={`traillab-candidate-${c.osm_id}`}>
                <div className="font-medium text-sm flex items-center gap-2"><Route className="w-4 h-4 text-primary" />{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.region_state || "—"} · {c.length_miles} mi</div>
              </button>
            ))}
          </div>

          {trail && (
            <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="traillab-corridor-builder">
              <div className="text-sm"><span className="font-bold">{trail.name}</span> <span className="text-muted-foreground">· {trail.length_miles} mi · {trail.segment_count} pts · OSM/ODbL</span></div>
              <div>
                <div className="text-xs text-muted-foreground mb-1.5">Corridor width</div>
                <div className="flex gap-2">{WIDTHS.map((w) => <Button key={w} size="sm" variant={width === w ? "default" : "outline"} onClick={() => genCorridor(w)} disabled={busy === "corridor"} data-testid={`traillab-width-${w}`}>{w} mi</Button>)}</div>
              </div>
              {corridor && (
                <>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Territory name" data-testid="traillab-name-input" />
                  <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" data-testid="traillab-notes-input" />
                  <div className="flex gap-2">
                    <Button className="flex-1" onClick={saveDraft} disabled={busy === "save"} data-testid="traillab-save-btn">{busy === "save" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{territoryId ? "Saved" : "Save draft"}</Button>
                    <Button className="flex-1" variant="outline" onClick={findBusinesses} disabled={busy === "biz" || !territoryId} data-testid="traillab-biz-btn">{busy === "biz" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}Find businesses</Button>
                  </div>
                </>
              )}
              {Object.keys(summary).length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="traillab-summary">
                  {Object.entries(summary).map(([k, v]) => <Badge key={k} className="bg-muted text-muted-foreground border border-border">{k}: {v}</Badge>)}
                </div>
              )}
            </div>
          )}

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-sm font-heading font-bold mb-2">Draft territories</div>
            {drafts.length === 0 ? <p className="text-xs text-muted-foreground">No drafts yet.</p> : drafts.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-border/50 last:border-0" data-testid={`traillab-draft-${d.id}`}>
                <button className="text-left text-sm hover:text-primary" onClick={() => openDraft(d.id)}>{d.name}<span className="text-xs text-muted-foreground ml-1">· {d.buffer_miles}mi</span></button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={() => deleteDraft(d.id)} data-testid={`traillab-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border overflow-hidden" data-testid="traillab-map">
          <LeafletMap height={620}>
            {bounds && <FitBounds bounds={bounds} nonce={`${territoryId || (trail && trail.id)}-${width}`} />}
            {trail && <Polyline positions={trail.coordinates} pathOptions={{ color: "#0ea5e9", weight: 5 }} />}
            {corridor && <Polygon positions={corridor} pathOptions={{ color: "#ef4444", fillColor: "#facc15", fillOpacity: 0.2, weight: 2 }} />}
            {businesses.map((b) => (
              <CircleMarker key={b.id} center={[b.lat, b.lng]} radius={5} pathOptions={{ color: "#fff", weight: 1, fillColor: CAT_COLORS[b.category] || "#38bdf8", fillOpacity: 1 }}>
                <Tooltip>{b.name} · {b.category}</Tooltip>
              </CircleMarker>
            ))}
          </LeafletMap>
        </div>
      </div>
    </div>
  );
}
